import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Users, Clock, Award } from "lucide-react";
import SectionBackground from "@/components/layout/SectionBackground";
import { useDanceLabEvents } from "@/hooks/useDanceLabEvents";
import { Skeleton } from "@/components/ui/skeleton";
import losAngelesImg from "@/assets/losangeles.webp";
import newYorkImg from "@/assets/newyork.webp";

const eventImages: Record<string, string> = {
  "los-angeles": losAngelesImg,
  "new-york": newYorkImg,
};

const DanceLab = () => {
  const navigate = useNavigate();
  const { data: danceLabEvents = [], isLoading } = useDanceLabEvents();

  return (
    <main className="relative min-h-screen">
      <SectionBackground />

      {/* HERO SECTION */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ paddingTop: "88px" }}
      >
        <div className="absolute inset-0 z-0 overflow-hidden" style={{ backgroundColor: "#000000" }}>
          <video
            autoPlay
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-40"
            src="/assets/dance-lab-herovid.webm"
          />
          <div className="absolute bottom-0 left-0 right-0 h-64" style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0.8) 60%, #000000 100%)" }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-8 md:px-12 lg:px-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-6 flex flex-col items-center gap-3"
          >
            <span
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #C9A961 100%)",
                boxShadow: "0 0 25px rgba(212, 175, 55, 0.4)",
                border: "1px solid rgba(244, 228, 193, 0.4)",
                fontFamily: "'Grift', sans-serif",
                color: "#1a1a1a",
              }}
            >
              <Award size={14} />
              Official Klau & Ros Certification
            </span>
            <span
              className="text-xs uppercase tracking-[0.15em]"
              style={{
                fontFamily: "'Grift', sans-serif",
                color: "rgba(255, 255, 255, 0.45)",
                fontStyle: "italic",
              }}
            >
              Exclusive Training · Via Application Only
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-6 leading-tight"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(36px, 7vw, 80px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              textShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
              fontStyle: "italic",
            }}
          >
            <span style={{ transform: "skewX(-5deg)", display: "inline-block" }}>
              Endless{" "}
              <span className="inline-block relative" style={{ color: "#E67E22" }}>
                Dance Lab
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ backgroundColor: "#E67E22" }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                />
              </span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-8 max-w-2xl mx-auto"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(16px, 2vw, 22px)",
              color: "rgba(255, 255, 255, 0.9)",
              letterSpacing: "0.02em",
              fontWeight: 400,
              lineHeight: 1.6,
              fontStyle: "italic",
            }}
          >
            Intensive multi-day workshops with Klau & Ros. Train, perform, and earn your official certification. Limited spots.
          </motion.p>


          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <button
              onClick={() => {
                document.getElementById("events-section")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-8 py-4 rounded-full font-bold text-base md:text-lg transition-all duration-300 flex items-center gap-2 group mx-auto"
              style={{
                background: "linear-gradient(90deg, #E67E22 0%, #FF9E4F 100%)",
                color: "#FFFFFF",
                fontFamily: "'Grift', sans-serif",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
                boxShadow: "0 4px 20px rgba(230, 126, 34, 0.4)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "skewX(-5deg) translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 30px rgba(230, 126, 34, 0.6)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "skewX(-5deg) translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(230, 126, 34, 0.4)";
              }}
            >
              <span>View Upcoming Labs</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* WHAT IS THE DANCE LAB */}
      <section className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <h2
              className="mb-8 md:mb-12 leading-tight"
              style={{
                fontFamily: "'Grift', sans-serif",
                fontSize: "clamp(24px, 4vw, 40px)",
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: "#FFFFFF",
                textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
              }}
            >
              What Is the{" "}
              <span className="inline-block relative" style={{ color: "#E67E22" }}>
                Dance Lab
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ backgroundColor: "#E67E22" }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </span>
              ?
            </h2>
            <div
              className="rounded-3xl p-8 md:p-12 leading-relaxed space-y-6"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(16px, 2vw, 20px)",
                color: "rgba(255, 255, 255, 0.9)",
                background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 8px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                fontStyle: "italic",
              }}
            >
              <p className="text-xl md:text-2xl" style={{ fontStyle: "normal", marginBottom: "1.5rem" }}>
                An intimate, multi-day intensive designed for dancers who want to go deeper.
              </p>
              <div className="space-y-5">
                <p>
                  The Endless Dance Lab is an exclusive workshop experience where a small group of selected dancers trains directly with Klau & Ros over multiple days.
                </p>
                <p>
                  Each edition focuses on styling, musicality, partnerwork, and performance with personalized feedback and hands-on coaching.
                </p>
                <p>
                  Spots are limited and applications are reviewed to ensure a high-quality, focused training environment.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section id="events-section" className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 md:mb-20 leading-tight text-left"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(24px, 4vw, 40px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
              fontStyle: "italic",
              transform: "skewX(-5deg)",
            }}
          >
            Upcoming{" "}
            <span className="inline-block relative" style={{ color: "#E67E22" }}>
              Labs
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{ backgroundColor: "#E67E22" }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>

          {isLoading ? (
            <div className="grid md:grid-cols-2 gap-8 md:gap-10">
              {[0, 1].map((i) => (
                <div key={i} className="rounded-3xl overflow-hidden min-h-[500px] md:min-h-[600px]" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <Skeleton className="w-full h-full" />
                </div>
              ))}
            </div>
          ) : (
          <div className="grid md:grid-cols-2 gap-8 md:gap-10">
            {danceLabEvents.map((event, index) => (
              <motion.div
                key={event.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                className="relative rounded-3xl overflow-hidden min-h-[500px] md:min-h-[600px] cursor-pointer group"
                style={{
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  boxShadow: "0 8px 40px rgba(0, 0, 0, 0.4)",
                  transition: "border-color 0.3s, box-shadow 0.3s",
                }}
                onClick={() => navigate(`/dance-lab/${event.slug}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.5)";
                  e.currentTarget.style.boxShadow = "0 12px 60px rgba(230, 126, 34, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)";
                  e.currentTarget.style.boxShadow = "0 8px 40px rgba(0, 0, 0, 0.4)";
                }}
              >
                {/* Background Image */}
                {eventImages[event.slug] && (
                  <img
                    src={eventImages[event.slug]}
                    alt={`${event.city} Dance Lab`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}

                {/* Gradient Overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(to top, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.92) 35%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.4) 80%, transparent 100%)",
                  }}
                />

                {/* Text Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                  <h3
                    className="text-3xl md:text-4xl font-bold mb-1"
                    style={{
                      fontFamily: "'Grift', sans-serif",
                      color: "#E67E22",
                      fontStyle: "italic",
                      transform: "skewX(-5deg)",
                    }}
                  >
                    {event.city}
                  </h3>

                  {event.subtitle && (
                    <p className="text-sm mb-0.5" style={{ fontFamily: "'Grift', sans-serif", color: "rgba(255,255,255,0.7)", fontStyle: "italic" }}>
                      {event.subtitle}
                    </p>
                  )}
                  {event.level && (
                    <p className="text-xs mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.5)", fontStyle: "italic" }}>
                      {event.level}
                    </p>
                  )}

                  {/* Certification indicator */}
                  <div className="flex items-center gap-1.5 mb-4">
                    <Award size={13} style={{ color: "#D4AF37" }} />
                    <span className="text-xs" style={{ fontFamily: "'Grift', sans-serif", color: "#D4AF37", fontStyle: "italic" }}>
                      Official Certificate
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5 text-sm" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.85)", fontStyle: "italic" }}>
                    <span className="flex items-center gap-1.5"><MapPin size={15} style={{ color: "rgba(255,255,255,0.6)" }} />{event.city}, {event.country}</span>
                    <span className="flex items-center gap-1.5"><Calendar size={15} style={{ color: "rgba(255,255,255,0.6)" }} />{event.dates}</span>
                    <span className="flex items-center gap-1.5"><Users size={15} style={{ color: "rgba(255,255,255,0.6)" }} />{event.spotsAvailable} spots</span>
                    {event.totalHours && <span className="flex items-center gap-1.5"><Clock size={15} style={{ color: "rgba(255,255,255,0.6)" }} />{event.totalHours}</span>}
                  </div>

                  <div className="flex items-center justify-between pt-5 border-t border-white/10">
                    <p
                      className="text-xl md:text-2xl font-bold"
                      style={{ fontFamily: "'Grift', sans-serif", color: "#E67E22", fontStyle: "italic", transform: "skewX(-5deg)" }}
                    >
                      From ${Math.min(...event.pricing.map((p) => p.price))}
                    </p>
                    <div
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm"
                      style={{
                        background: "linear-gradient(90deg, #E67E22 0%, #FF9E4F 100%)",
                        color: "#FFFFFF",
                        fontFamily: "'Grift', sans-serif",
                        fontStyle: "italic",
                        transform: "skewX(-5deg)",
                      }}
                    >
                      <span>View Details</span>
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="rounded-3xl p-12 md:p-16 text-center"
            style={{
              background: "linear-gradient(135deg, rgba(230, 126, 34, 0.15) 0%, rgba(255, 158, 79, 0.08) 100%)",
              backdropFilter: "blur(20px)",
              border: "2px solid rgba(230, 126, 34, 0.3)",
              boxShadow: "0 8px 60px rgba(230, 126, 34, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
            }}
          >
            <h2
              className="mb-4 leading-tight"
              style={{
                fontFamily: "'Grift', sans-serif",
                fontSize: "clamp(22px, 3.5vw, 36px)",
                fontWeight: 700,
                color: "#FFFFFF",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
              }}
            >
              Have Questions?
            </h2>
            <p
              className="mb-8 text-lg"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: "rgba(255, 255, 255, 0.8)",
                fontStyle: "italic",
              }}
            >
              Reach out to us and we'll help you find the right lab for you.
            </p>
            <a
              href="mailto:klauyros@gmail.com?subject=Dance Lab Inquiry"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all duration-300"
              style={{
                background: "linear-gradient(90deg, #E67E22 0%, #FF9E4F 100%)",
                color: "#FFFFFF",
                fontFamily: "'Grift', sans-serif",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
                boxShadow: "0 4px 20px rgba(230, 126, 34, 0.4)",
              }}
            >
              <span>Contact Us</span>
              <ArrowRight size={18} />
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default DanceLab;
